import type { GuidePage } from '../guides';

const example = [
  { item: 'Materials: 150 ft of 6 ft cedar privacy (posts, rails, pickets), concrete, fasteners, delivery', amount: '$3,150' },
  { item: 'Gate kit and hardware for one 4 ft walk gate', amount: '$160' },
  { item: 'Labor: 3-person crew x 2 days (dig and set posts day 1, rails, pickets and gate day 2)', amount: '$2,300' },
  { item: 'Equipment and disposal: auger rental share, tear-out of old fence, dump fee', amount: '$480' },
  { item: 'Overhead share and margin for residential fence work', amount: '$1,510' },
];

const faq = [
  {
    q: 'How do fence contractors price per linear foot?',
    a: 'They build a per-foot figure from their own jobs: materials per foot for each style and height, plus labor per foot at their crew\'s pace, plus overhead and margin. Gates, corners, tear-out and slopes are then added on top, because they do not scale with length.',
  },
  {
    q: 'Why are gates priced separately?',
    a: 'A gate takes heavier posts, more hardware and more careful work than the same width of fence, and it is where callbacks happen if it sags. Pricing it as its own line covers that time.',
  },
  {
    q: 'How do slopes change the price?',
    a: 'Stepped or racked fences on a grade take longer to lay out and build, and stepped sections use more material at the bottom. Add a slope adjustment for those runs rather than averaging it across the whole job.',
  },
  {
    q: 'Should I include tear-out of the old fence?',
    a: 'If there is one, yes, as its own line. Removing posts set in concrete can take as long as setting new ones, and disposal costs money.',
  },
];

const page: GuidePage = {
  slug: 'how-to-price-a-fence',
  path: '/resources/how-to-price-a-fence',
  primaryKeyword: 'how to price a fence job',
  secondaryKeywords: ['fence pricing per linear foot', 'how to estimate fence installation', 'fence contractor pricing'],
  title: 'How to Price a Fence Job by the Linear Foot | Scopie', // 51
  metaDescription:
    'Build fence prices from materials per foot, crew pace, gates, corners, slopes, tear-out, overhead and margin, with a worked cedar privacy fence example.', // 151
  h1: 'How to price a fence job',
  answerFirst:
    'Price a fence from materials per foot for the style and height, labor per foot at your crew\'s real pace, then add gates, corners, slopes and tear-out as separate items, since they do not scale with length. Finish with overhead and margin. The cedar privacy fence example below shows the whole calculation.',
  datePublished: '2026-10-07',
  lastReviewed: '2026-10-07',
  schemaType: 'Article',
  blocks: [
    {
      type: 'callout',
      id: 'per-foot',
      text: 'A per-foot price is an output, not an input. Work it out from your own materials and crew pace, then use it to quote quickly.',
    },
    {
      type: 'sample-quote',
      id: 'example',
      h2: 'Worked example: 150 ft of cedar privacy fence',
      label: 'Costing one backyard fence (internal numbers)',
      lines: example,
      total: '$7,600',
      totalLabel: 'Price to quote',
      note: 'Illustrative numbers. Lumber prices and labor costs vary by region and season.',
    },
    {
      type: 'list',
      id: 'steps',
      h2: 'The calculation, step by step',
      style: 'steps',
      items: [
        { title: 'Materials per foot', text: 'Posts at your spacing, rails, pickets or panels, concrete per post, fasteners. Price each style and height you sell.' },
        { title: 'Labor per foot', text: 'From your own jobs: feet of fence a crew completes per day for that style, including digging and setting posts.' },
        { title: 'Add the fixed items', text: 'Gates, corner and end posts, slope adjustments and tear-out, each priced on its own.' },
        { title: 'Equipment and disposal', text: 'Auger or machine time, trailer, dump fees.' },
        { title: 'Overhead and margin', text: 'A share of trucks, insurance and office, then the profit you want.' },
      ],
    },
    {
      type: 'text',
      id: 'yard',
      h2: 'What the yard does to the number',
      paragraphs: [
        'Rocky or root-filled ground slows post holes dramatically. Tight access means carrying every panel by hand. Long straight runs on flat ground are the fastest work you will do; short runs with many corners and grade changes are the slowest. Note these at the site visit and adjust labor for the runs they affect.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Fence pricing mistakes',
      style: 'mistakes',
      items: [
        { title: 'One per-foot price for every style', text: 'Picket, privacy, vinyl and aluminum have very different material and labor per foot.' },
        { title: 'Gates priced as fence', text: 'They need their own time and hardware.' },
        { title: 'Tear-out underestimated', text: 'Old posts in concrete are slow to remove.' },
        { title: 'Material prices from last season', text: 'Lumber moves; refresh your per-foot figures regularly and date your quotes.' },
      ],
    },
    {
      type: 'text',
      id: 'quote',
      h2: 'Putting it on the quote',
      paragraphs: [
        'In Scopie, save each style and height as an item at your per-foot price and gates as their own items, then build the quote run by run. Extras such as staining or post caps can be optional lines. The quote is emailed to the homeowner, who ticks the extras and signs before you order materials.',
      ],
      links: [{ phrase: 'build the quote run by run', href: '/for/fencing' }],
    },
    { type: 'faq', id: 'faq', h2: 'Fence pricing questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/fencing', anchor: 'Scopie for fence contractors', status: 'live' },
    { href: '/templates/fence-estimate-template', anchor: 'fence estimate template', status: 'live' },
    { href: '/resources', anchor: 'All pricing guides', status: 'live' },
  ],
  cta: {
    heading: 'Quote fences at your real per-foot price',
    text: 'Save your styles, heights and gates as items in Scopie, and email each fence quote from the yard.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
